import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./BillingFindVoucherModal.css";

const PAGE_SIZE = 20;

function formatDate(val) {
  if (!val) return "—";
  try {
    const d =
      typeof val === "object" && typeof val.toDate === "function"
        ? val.toDate()
        : new Date(val);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN");
  } catch {
    return "—";
  }
}

function formatMoney(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
}

function exportVouchersCsv(rows) {
  const cols = ["VoucherNo", "Date", "Customer", "Employee", "Lines", "Week", "Amount"];
  const lines = [cols.join(",")];
  for (const r of rows) {
    const vals = [
      r.voucherNo || "",
      formatDate(r.date),
      `"${String(r.party || "").replace(/"/g, '""')}"`,
      `"${String(r.employeeName || "").replace(/"/g, '""')}"`,
      r.lineCount ?? r.items?.length ?? 0,
      r.importPeriodKey || "",
      Number(r.grandTotal ?? r.totals?.net ?? 0).toFixed(2),
    ];
    lines.push(vals.join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `outward_vouchers_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Find → Outward Supply voucher picker (sales import lineChunks).
 */
export default function BillingFindVoucherModal({
  open,
  onClose,
  loading,
  onRefresh,
  vouchers = [],
  voucherTypeLabel = "Outward Supply",
  onOpenVoucher,
}) {
  const [search, setSearch] = useState("");
  const [weekFilter, setWeekFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setPage(1);
      setSelectedId(null);
    }
  }, [open, search, weekFilter, fromDate, toDate]);

  const weekOptions = useMemo(() => {
    const set = new Set();
    for (const v of vouchers) {
      const pk = String(v.importPeriodKey || "").trim();
      if (pk) set.add(pk);
    }
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [vouchers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return vouchers.filter((inv) => {
      if (weekFilter && String(inv.importPeriodKey || "") !== weekFilter) return false;
      if (fromDate) {
        const d = inv.date ? new Date(inv.date) : null;
        if (!d || Number.isNaN(d.getTime()) || d < new Date(`${fromDate}T00:00:00`)) return false;
      }
      if (toDate) {
        const d = inv.date ? new Date(inv.date) : null;
        if (!d || Number.isNaN(d.getTime()) || d > new Date(`${toDate}T23:59:59`)) return false;
      }
      if (!q) return true;
      return (
        String(inv.voucherNo || "").toLowerCase().includes(q) ||
        String(inv.party || inv.partyName || "").toLowerCase().includes(q) ||
        String(inv.employeeName || "").toLowerCase().includes(q)
      );
    });
  }, [vouchers, search, weekFilter, fromDate, toDate]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sortBy === "date") {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        return (da - db) * dir;
      }
      if (sortBy === "amount") {
        const amA = Number(a.grandTotal ?? a.totals?.net ?? 0);
        const amB = Number(b.grandTotal ?? b.totals?.net ?? 0);
        return (amA - amB) * dir;
      }
      if (sortBy === "lines") {
        return ((a.lineCount ?? 0) - (b.lineCount ?? 0)) * dir;
      }
      const va = String(a[sortBy] || "").toLowerCase();
      const vb = String(b[sortBy] || "").toLowerCase();
      return va.localeCompare(vb, undefined, { numeric: true }) * dir;
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const totalAmount = useMemo(
    () => filtered.reduce((s, inv) => s + Number(inv.grandTotal ?? inv.totals?.net ?? 0), 0),
    [filtered]
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, page]);

  const toggleSort = useCallback(
    (col) => {
      if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      else {
        setSortBy(col);
        setSortDir(col === "voucherNo" ? "asc" : "desc");
      }
    },
    [sortBy]
  );

  const sortMark = (col) => (sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  const openSelected = useCallback(
    (inv) => {
      if (!inv) return;
      onOpenVoucher?.(inv);
      onClose?.();
    },
    [onOpenVoucher, onClose]
  );

  if (!open) return null;

  return (
    <div
      className="bfv-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bfv-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="bfv-modal" onClick={(e) => e.stopPropagation()}>
        <header className="bfv-header">
          <div>
            <h2 id="bfv-title">Find voucher — {voucherTypeLabel}</h2>
            <p>
              Exact <strong>{voucherTypeLabel}</strong> vouchers from weekly sales import. Double-click or
              Open to edit on this screen.
            </p>
          </div>
          <div className="bfv-header-actions">
            <button type="button" className="bfv-btn bfv-btn-ghost" onClick={() => onRefresh?.()} disabled={loading}>
              Refresh
            </button>
            <button type="button" className="bfv-btn bfv-btn-close" onClick={() => onClose?.()}>
              Close
            </button>
          </div>
        </header>

        <div className="bfv-stats">
          <div className="bfv-stat">
            <div className="bfv-stat-label">Vouchers</div>
            <div className="bfv-stat-value">{vouchers.length.toLocaleString()}</div>
          </div>
          <div className="bfv-stat">
            <div className="bfv-stat-label">Matching filters</div>
            <div className="bfv-stat-value">{filtered.length.toLocaleString()}</div>
          </div>
          <div className="bfv-stat">
            <div className="bfv-stat-label">Total amount</div>
            <div className="bfv-stat-value">{formatMoney(totalAmount)}</div>
          </div>
        </div>

        <div className="bfv-toolbar">
          <input
            type="search"
            className="bfv-search"
            placeholder="Search voucher no, customer, employee…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={weekFilter} onChange={(e) => setWeekFilter(e.target.value)} title="Upload week">
            <option value="">All weeks</option>
            {weekOptions.map((pk) => (
              <option key={pk} value={pk}>
                {pk}
              </option>
            ))}
          </select>
          <label className="text-xs text-slate-500">
            From
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="ml-1" />
          </label>
          <label className="text-xs text-slate-500">
            To
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="ml-1" />
          </label>
          <button type="button" className="bfv-btn bfv-btn-ghost" style={{ color: "#059669", borderColor: "#a7f3d0", background: "#ecfdf5" }} onClick={() => exportVouchersCsv(filtered)}>
            Export CSV
          </button>
        </div>

        <div className="bfv-table-wrap">
          {loading ? (
            <div className="bfv-loading">
              <div className="bfv-spinner" />
              Loading vouchers from sales import…
            </div>
          ) : null}

          {!loading && paged.length === 0 ? (
            <div className="bfv-empty">
              <strong>No vouchers found</strong>
              Upload weekly data in Import Center, or adjust search / week / date filters.
            </div>
          ) : (
            <table className="bfv-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort("voucherNo")}>Voucher no{sortMark("voucherNo")}</th>
                  <th onClick={() => toggleSort("date")}>Date{sortMark("date")}</th>
                  <th>Customer</th>
                  <th>Employee</th>
                  <th className="bfv-center" onClick={() => toggleSort("lines")}>
                    Lines{sortMark("lines")}
                  </th>
                  <th>Week</th>
                  <th className="bfv-num" onClick={() => toggleSort("amount")}>
                    Amount{sortMark("amount")}
                  </th>
                  <th className="bfv-center"> </th>
                </tr>
              </thead>
              <tbody>
                {paged.map((inv) => {
                  const amount = Number(inv.grandTotal ?? inv.totals?.net ?? 0);
                  const lines = inv.lineCount ?? inv.items?.length ?? 0;
                  const isSel = selectedId === inv.id;
                  return (
                    <tr
                      key={inv.id}
                      className={isSel ? "bfv-selected" : ""}
                      onClick={() => setSelectedId(inv.id)}
                      onDoubleClick={() => openSelected(inv)}
                    >
                      <td>
                        <strong>{inv.voucherNo || "—"}</strong>
                      </td>
                      <td>{formatDate(inv.date)}</td>
                      <td className="bfv-truncate" title={inv.party || inv.partyName || ""}>
                        {inv.party || inv.partyName || "—"}
                      </td>
                      <td className="bfv-truncate" title={inv.employeeName || ""}>
                        {inv.employeeName || "—"}
                      </td>
                      <td className="bfv-center">{lines}</td>
                      <td>
                        {inv.importPeriodKey ? (
                          <span className="bfv-pill">{inv.importPeriodKey}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="bfv-num">{formatMoney(amount)}</td>
                      <td className="bfv-center">
                        <button
                          type="button"
                          className="bfv-open-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSelected(inv);
                          }}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p className="bfv-hint">Tip: click a row to select, double-click or Open to load into Outward Supply. Save updates sales import only.</p>

        <footer className="bfv-footer">
          <span className="text-sm text-slate-600">
            Page {page} of {totalPages} · {PAGE_SIZE} per page
          </span>
          <div className="bfv-pager">
            <button type="button" disabled={page <= 1} onClick={() => setPage(1)}>
              First
            </button>
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </button>
            <span className="text-sm px-2">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
              Last
            </button>
            {selectedId ? (
              <button
                type="button"
                className="bfv-open-btn"
                style={{ marginLeft: "0.5rem" }}
                onClick={() => {
                  const inv = sorted.find((x) => x.id === selectedId);
                  openSelected(inv);
                }}
              >
                Open selected
              </button>
            ) : null}
          </div>
        </footer>
      </div>
    </div>
  );
}
